/** GET /api/v2/approval/{documentId}/line-members 응답 1건 */
export type ApprovalLineMemberStatusResponse = {
  intranetId: string;
  /** 프로필이 없는 인물은 null */
  passportId: string | null;
  /** 재직 1 / 퇴사 0. 프로필을 찾지 못한 인물도 0 으로 내려온다 */
  activated: number;
  isOnLeave: boolean;
  leaveType: string | null;
  leaveTypeName: string | null;
};

/** 조회 화면 응답의 인물 항목에서 매칭에 쓰는 키만 */
export type ApprovalLineMemberKey = {
  intranetId?: string | null;
  passportId?: string | null;
};

export type ApprovalLineMemberState = {
  deactivated: boolean;
  onLeave: boolean;
};

export const NO_MEMBER_STATE: ApprovalLineMemberState = {
  deactivated: false,
  onLeave: false,
};

/**
 * 결재가 더 진행되지 않는 문서 상태 — C(완료)·R(반려. 회수도 R 로 전환)·X(취소)·D(삭제).
 * 퇴사·휴직 배지는 "결재가 멈추니 조치하라" 는 안내라 이 상태의 문서에는 싣지 않음(v1 동일).
 */
const LINE_MEMBER_BADGE_HIDDEN_STATUSES: readonly string[] = [
  'C',
  'R',
  'X',
  'D',
];

/**
 * 조회 화면 결재라인의 퇴사·휴직 배지 노출 여부. 상태 미전달은 노출(누락보다 과표시가 안전).
 * 인자를 string 으로 받는 이유는 referrerAddable 과 동일 — 목록 응답의 status 가 string.
 */
export const isLineMemberBadgeVisible = (
  documentStatus: string | null | undefined,
): boolean =>
  !documentStatus ||
  !LINE_MEMBER_BADGE_HIDDEN_STATUSES.includes(documentStatus);

/**
 * 겸직이면 같은 intranetId 가 프로필별로 나뉘므로 passportId 까지 붙인 키를 우선 쓴다.
 * 응답에 passportId 가 없는 인물도 있어 단독 키로 폴백할 수 있게 함께 색인한다.
 */
const pairKey = (intranetId: string, passportId: string) =>
  `${intranetId}::${passportId}`;

/**
 * 응답을 매칭용 색인으로. 한 사람이 최대 세 키(쌍/intranetId/passportId)로 들어간다.
 * 단독 키는 먼저 들어온 값을 남긴다 — 겸직으로 여러 건이 올 때 어느 쪽을 고를 근거가
 * 없으므로, 정확한 매칭은 쌍 키가 담당하고 단독 키는 어디까지나 폴백이다.
 */
export const indexLineMemberStatuses = (
  members: ApprovalLineMemberStatusResponse[] | undefined,
): Map<string, ApprovalLineMemberState> => {
  const map = new Map<string, ApprovalLineMemberState>();
  (members ?? []).forEach((m) => {
    const state: ApprovalLineMemberState = {
      deactivated: m.activated !== 1,
      onLeave: m.isOnLeave === true,
    };
    if (m.intranetId && m.passportId) {
      map.set(pairKey(m.intranetId, m.passportId), state);
    }
    if (m.intranetId && !map.has(m.intranetId)) {
      map.set(m.intranetId, state);
    }
    if (m.passportId && !map.has(m.passportId)) {
      map.set(m.passportId, state);
    }
  });
  return map;
};

/** 색인에서 인물 1명의 상태를 찾는다. 못 찾으면 '해당 없음'. */
export const lookupLineMemberState = (
  index: Map<string, ApprovalLineMemberState>,
  person: ApprovalLineMemberKey,
): ApprovalLineMemberState => {
  const { intranetId, passportId } = person;
  if (intranetId && passportId) {
    const exact = index.get(pairKey(intranetId, passportId));
    if (exact) return exact;
  }
  return (
    (intranetId && index.get(intranetId)) ||
    (passportId && index.get(passportId)) ||
    NO_MEMBER_STATE
  );
};

/**
 * 폴백으로 내려갈지 판단한다. 조건은 '오류가 났고 아직 한 번도 받지 못했을 때' 다.
 *
 * SWR 은 재검증이 실패해도 직전 응답을 그대로 들고 있으므로, 오류만 보고 판단하면
 * focus/reconnect 재검증 한 번에 멀쩡한 판정을 버리고 배지가 통째로 사라진다.
 */
export const shouldUseLineMemberFallback = (
  error: unknown,
  data: ApprovalLineMemberStatusResponse[] | undefined,
): boolean => Boolean(error) && data === undefined;

/**
 * 신규 API 를 못 쓸 때의 폴백 — 기존 문서 단위 조회는 퇴사자 passportId 만 준다.
 * 휴직은 알 수 없으므로 표시하지 않는다.
 */
export const lineMemberStateFromDeactivatedIds = (
  deactivatedIds: Set<string>,
  person: ApprovalLineMemberKey,
): ApprovalLineMemberState => ({
  deactivated: !!person.passportId && deactivatedIds.has(person.passportId),
  onLeave: false,
});
